import { jsx, jsxs } from "react/jsx-runtime";
import { __css, tokens, useFluent } from "@fluentui/react-components";
import { createContext, useContext, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useStore } from "zustand";
import { listPaneIsDrawer, rightPanelIsOverlay, useShellTier } from "./layout.js";
import { createShellStore } from "./shell-store.js";
import { layout } from "../theme/tokens.js";
import { box } from "./styles.js";
const MAIN_CONTENT_ID = "bf-main";
const ShellContext = createContext(null);
function useShell() {
  const value = useContext(ShellContext);
  if (!value) throw new Error("Shell components must be rendered inside <AppShell>");
  return value;
}
function useShellState(selector) {
  return useStore(useShell().store, selector);
}
function useToggleListPane() {
  const { tier, store } = useShell();
  return () => {
    const s = store.getState();
    if (listPaneIsDrawer(tier)) s.setListDrawerOpen(!s.listDrawerOpen);
    else s.setListPaneCollapsed(!s.listPaneCollapsed);
  };
}
const useStyles = __css({"container":{"Be3zhsn":"f138vucn","Bhtl8mj":"f1xzdw6b","st59p4":"f1bxbdjk","qttt6h":"fl4vidq","B68tc82":0,"Bmxbyg5":0,"Bpg54ce":"f1a3p1vp","De3pzq":"f16xq7d1","sj55zd":"f19n0e5"},"frame":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","st59p4":"f1b70jmh","qttt6h":"fl4vidq"},"body":{"mc9l5x":"f22iagw","Bh6795r":"fqerorx","qttt6h":"fl4vidq","i8kkvl":0,"Belr9w4":0,"rmohyg":"f19gb1f4","Bnt446c":"f6yclgq","Dbcxam":"f1petgab","Bw6cc19":0,"Bj04r6y":0,"Bdehfym":"f1lmo028","lvthp1":0,"vgo7ak":0,"Blegc4d":0,"Jtbvhq":0,"xa482l":"frj5bw"},"main":{"Bh6795r":"fqerorx","Bgto7am":"fuzgk9b","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","De3pzq":"fxugw4r","B4m11q7":"f32hy0g","rm1204":"fwyddsq","ijz38e":"f1ftuoky","tzxy30":"fgmlvap","Bwezv66":"fwl1lcr","Ctt3hk":"f1crbv9w","gggo0z":"fi15kbk","B834pou":"f1taw3pw","F1poyb":"foi3q75","Behc1zw":"f10zy6vx","uk9mye":"f1p2dij1","mbw9mh":"f1a566p","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"f1o0qvyv","E5pizo":"f1whvlc6","oeaueh":"f1s6fcnf","Bbd2tiy":0,"B8sxo35":0,"By0nz3n":0,"il3xun":0,"Bhp5cob":"f1gib8g7","vprpgd":"f1xdx49n","Iidik3":"f1oezo70","Bdv43b9":"f84ubuw","fm0d51":"f8plgb7"},"skipLink":{"qhf8xq":"f1euv43f","B3s2vv4":"f19xaomy","Bwk1v6v":"f3tnz4x","Bj3rh1h":"f1buevke","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f9b8bpc","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f12n3xlw","De3pzq":"fxugw4r","sj55zd":"fyind8e","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5","E5pizo":"f1hg901r","B84k19c":"f1q8g8k4"}});
function AppShell({
  header,
  appBar,
  listPane,
  rightPanel,
  children,
  store,
  tier: forcedTier
}) {
  const styles = useStyles();
  const { t } = useTranslation("ui");
  const { dir } = useFluent();
  const ref = useRef(null);
  const tier = useShellTier(ref, forcedTier);
  const shellStore = useMemo(() => store ?? createShellStore(), [store]);
  const hasListPane = listPane !== void 0 && listPane !== null;
  const value = useMemo(
    () => ({ tier, dir, store: shellStore, hasListPane }),
    [tier, dir, shellStore, hasListPane]
  );
  const phone = tier === "phone";
  return /* @__PURE__ */ jsx(ShellContext.Provider, { value, children: /* @__PURE__ */ jsxs("div", { ref, className: styles.container, "data-shell-tier": tier, children: [
    /* @__PURE__ */ jsx("a", { className: styles.skipLink, href: `#${MAIN_CONTENT_ID}`, children: t("shell.skipToContent") }),
    /* @__PURE__ */ jsxs("div", { className: styles.frame, children: [
      header,
      /* @__PURE__ */ jsxs("div", { className: styles.body, children: [
        !phone && appBar,
        listPane,
        /* @__PURE__ */ jsx("main", { id: MAIN_CONTENT_ID, tabIndex: -1, className: styles.main, children }),
        rightPanel
      ] }),
      phone && appBar
    ] })
  ] }) });
}
export {
  AppShell,
  MAIN_CONTENT_ID,
  listPaneIsDrawer,
  rightPanelIsOverlay,
  useShell,
  useShellState,
  useToggleListPane
};
//# sourceMappingURL=app-shell.js.map

import "/opt/batiflow/app/packages/ui/dist/shell/app-shell.griffel.css";